// INVCOUNT2-S2 (docs/build-briefs/INVCOUNT2-S2-count-table-screen.md, PRD P0.7). Pure, no DOM: the count table's
// arithmetic, grouping, filtering, sorting and review rows, so every rule here is provable in a plain node:test file
// (test/invcount2-s2-count-table-model.test.ts) without a browser.
//
// Entry is cases + bottles + a tenth of a bottle. The server converts and stores it in bottles
// (src/domain/countTable.ts, recordTableCount); this file does the same exact integer-tenths arithmetic only to SHOW the
// total, never to decide what is stored.

import type {
  CountObservationDto,
  CountSessionReviewRowDto,
  CountTableItemDto,
  CountUncountedItemDto,
} from "./api.ts";
import { compareNames } from "./nameSort.ts";
import { dollarVariance, needsCheck, percentOff } from "../../src/domain/countReviewValue.ts";

/** What a counter has entered for one item. Whole numbers only, chosen by tap. */
export interface TableEntry {
  cases: number;
  bottles: number;
  /** 0 to 9. */
  tenths: number;
  /** INVDESIGN1-S1: 0 to 9 hundredths beyond the tenth. Absent means 0. Only a keg counted by quarters uses it (1/4 is
   * .25); the table's own entry never sets it. */
  hundredths?: number;
}

export const MAX_ENTRY_WHOLE = 100000;
export const TENTHS_OPTIONS: readonly number[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

export interface TableRow {
  id: string;
  name: string;
  itemKind: string;
  category: string;
  /** "750 ml bottle", or null when the item has no size. */
  sizeText: string | null;
  caseSize: number | null;
  sectionId: string | null;
  sectionName: string | null;
  bottleOunces: string | null;
  /** INVDESIGN1-S1: "keg" for a keg row, which the area layout counts by fraction. */
  containerType: string | null;
  /** INVDESIGN1-S1: "Sixth barrel keg" style words for a keg, or null. */
  kegSize: string | null;
  /** The last closed count in bottles. undefined: a blind session, so there is no such figure at all. null: no earlier
   * count, or one with no bottle figure. */
  lastBottles: string | null | undefined;
  /** INVPOLISH1: this location's par for the item, or absent/null when it has none. */
  par?: string | null;
  /** What is shown now. null: not counted. */
  entry: TableEntry | null;
  /** What the server last confirmed. */
  savedEntry: TableEntry | null;
}

export function totalTenths(entry: TableEntry, caseSize: number | null): number {
  return (entry.cases * (caseSize ?? 0) + entry.bottles) * 10 + entry.tenths;
}

/** The entry in hundredths of a bottle, exact. */
export function totalHundredths(entry: TableEntry, caseSize: number | null): number {
  return totalTenths(entry, caseSize) * 10 + (entry.hundredths ?? 0);
}

/** 125 hundredths reads "1.25"; 130 reads "1.3"; 100 reads "1". */
export function formatHundredths(hundredths: number): string {
  const whole = Math.floor(hundredths / 100);
  const part = hundredths % 100;
  if (part === 0) return String(whole);
  const digits = String(part).padStart(2, "0").replace(/0$/, "");
  return `${whole}.${digits}`;
}

/** 133 tenths reads "13.3"; 130 reads "13". */
export function formatTenths(tenths: number): string {
  const whole = Math.floor(tenths / 10);
  const part = tenths % 10;
  return part === 0 ? String(whole) : `${whole}.${part}`;
}

export function entryTotalText(entry: TableEntry | null, caseSize: number | null): string | null {
  return entry === null ? null : formatHundredths(totalHundredths(entry, caseSize));
}

/** Splits a total back into cases, bottles and a tenth. Without a case size every whole bottle stays a bottle. */
export function entryFromTenths(total: number, caseSize: number | null): TableEntry {
  const whole = Math.floor(total / 10);
  const tenths = total % 10;
  if (caseSize === null || caseSize <= 0) return { cases: 0, bottles: whole, tenths };
  return { cases: Math.floor(whole / caseSize), bottles: whole % caseSize, tenths };
}

/** A quantity string ("13.3", "1.25") as tenths of a bottle, or undefined when it is not a plain non-negative number.
 * A figure finer than a tenth is rounded to the nearest tenth: it can only come from an ounce count made on the walk. */
function quantityToTenths(quantity: string): number | undefined {
  const value = Number(quantity);
  if (!Number.isFinite(value) || value < 0) return undefined;
  return Math.round(value * 10);
}

function quantityToHundredths(quantity: string): number | undefined {
  const value = Number(quantity);
  if (!Number.isFinite(value) || value < 0) return undefined;
  return Math.round(value * 100);
}

/** This session's saved count line as an entry, or null when it cannot be read as bottles (an item counted in ounces
 * with no bottle size). A line stored in ounces after a bottle entry (INV37) reads from the bottles the counter typed. */
export function entryFromObservation(
  observation: Pick<
    CountObservationDto,
    "counted_quantity" | "unit" | "entered_quantity" | "entered_unit"
  >,
  bottleOunces: string | null,
  caseSize: number | null,
  /** INVDESIGN1-S1: read to the hundredth (a keg counted by quarters), not the nearest tenth. */
  precise = false,
): TableEntry | null {
  const scale = precise ? 100 : 10;
  const toScaled = precise ? quantityToHundredths : quantityToTenths;
  let scaled: number | undefined;
  if (observation.entered_unit === "bottle" && observation.entered_quantity != null) {
    scaled = toScaled(observation.entered_quantity);
  } else if (observation.unit === "bottle" || observation.unit === "unit") {
    scaled = toScaled(observation.counted_quantity);
  } else if (observation.unit === "oz" && bottleOunces !== null) {
    const ounces = Number(observation.counted_quantity);
    const perBottle = Number(bottleOunces);
    if (Number.isFinite(ounces) && Number.isFinite(perBottle) && perBottle > 0) {
      scaled = Math.round((ounces / perBottle) * scale);
    }
  }
  if (scaled === undefined) return null;
  if (!precise) return entryFromTenths(scaled, caseSize);
  const entry = entryFromTenths(Math.floor(scaled / 10), caseSize);
  const hundredths = scaled % 10;
  return hundredths === 0 ? entry : { ...entry, hundredths };
}

export type EntryField = "cases" | "bottles";

/** One stepper tap. A row that is not counted starts from zero. Never below zero or above the server's limit. */
export function stepEntry(entry: TableEntry | null, field: EntryField, delta: 1 | -1): TableEntry {
  const base = entry ?? zeroEntry();
  const next = Math.min(MAX_ENTRY_WHOLE, Math.max(0, base[field] + delta));
  return { ...base, [field]: next };
}

export function setEntryTenths(entry: TableEntry | null, tenths: number): TableEntry {
  if (!Number.isInteger(tenths) || tenths < 0 || tenths > 9) {
    throw new Error(`tenths must be an integer from 0 to 9; received ${tenths}`);
  }
  return { ...(entry ?? zeroEntry()), tenths };
}

/** The None button: a deliberate zero, which is a count and not a gap. */
export function zeroEntry(): TableEntry {
  return { cases: 0, bottles: 0, tenths: 0 };
}

export function entriesEqual(a: TableEntry | null, b: TableEntry | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.cases === b.cases &&
    a.bottles === b.bottles &&
    a.tenths === b.tenths &&
    (a.hundredths ?? 0) === (b.hundredths ?? 0)
  );
}

/** "750 ml bottle", "12 oz can". Null when there is no size to show. */
export function sizeText(
  item: Pick<CountTableItemDto, "sizeValue" | "sizeUnit" | "containerType">,
  containerLabel: (containerType: string) => string,
): string | null {
  const container =
    item.containerType === null ? null : containerLabel(item.containerType).toLowerCase();
  if (item.sizeValue === null || item.sizeUnit === null) return container;
  const value = Number(item.sizeValue);
  const shown = Number.isFinite(value) ? String(value) : item.sizeValue;
  return `${shown} ${item.sizeUnit}${container === null ? "" : ` ${container}`}`;
}

export function rowFromItem(
  item: CountTableItemDto,
  observation: CountObservationDto | undefined,
  containerLabel: (containerType: string) => string,
): TableRow {
  const entry =
    observation === undefined
      ? null
      : entryFromObservation(
          observation,
          item.bottleOunces,
          item.caseSize,
          item.containerType === "keg",
        );
  return {
    id: item.id,
    name: item.name,
    itemKind: item.itemKind,
    category: item.category,
    sizeText: sizeText(item, containerLabel),
    caseSize: item.caseSize,
    sectionId: item.countSectionId,
    sectionName: item.countSectionName,
    bottleOunces: item.bottleOunces,
    containerType: item.containerType,
    kegSize: item.defaultKegSize ?? null,
    // A blind response has no lastCount key at all: that is "no such column", not "no earlier count".
    lastBottles: "lastCount" in item ? (item.lastCount?.inBottles ?? null) : undefined,
    par: item.par ?? null,
    entry,
    savedEntry: entry,
  };
}

// ---------------------------------------------------------------------
// Tabs, filters, sort
// ---------------------------------------------------------------------

export const ALL_TAB = "all";
export const UNASSIGNED_TAB = "unassigned";

export interface TableTab {
  id: string;
  label: string;
  counted: number;
  total: number;
}

export function tabIdForRow(row: Pick<TableRow, "sectionId">): string {
  return row.sectionId ?? UNASSIGNED_TAB;
}

/** All, then each section in its configured order, then Unassigned last. */
export function buildTabs(
  rows: readonly TableRow[],
  sections: readonly { id: string; name: string }[],
): TableTab[] {
  const tally = (predicate: (row: TableRow) => boolean) => {
    const inTab = rows.filter(predicate);
    return { counted: inTab.filter((row) => row.entry !== null).length, total: inTab.length };
  };
  return [
    { id: ALL_TAB, label: "All", ...tally(() => true) },
    ...sections.map((section) => ({
      id: section.id,
      label: section.name,
      ...tally((row) => row.sectionId === section.id),
    })),
    { id: UNASSIGNED_TAB, label: "Unassigned", ...tally((row) => row.sectionId === null) },
  ];
}

/** "x of y", or a dash for a tab with nothing in it. */
export function tabCountText(tab: Pick<TableTab, "counted" | "total">): string {
  return tab.total === 0 ? "-" : `${tab.counted} of ${tab.total}`;
}

export type TableSort = "location" | "name" | "last";

export interface TableFilters {
  tabId: string;
  /** An item kind, or null for all. */
  type: string | null;
  /** A category, or null for all. */
  subtype: string | null;
  sort: TableSort;
}

function lastValue(row: TableRow): number | undefined {
  if (row.lastBottles === undefined || row.lastBottles === null) return undefined;
  const value = Number(row.lastBottles);
  return Number.isFinite(value) ? value : undefined;
}

/** `rows` arrive in shelf order from the server (section, then position, Unassigned last), and Location keeps that. */
export function visibleRows(rows: readonly TableRow[], filters: TableFilters): TableRow[] {
  const shown = rows.filter(
    (row) =>
      (filters.tabId === ALL_TAB || tabIdForRow(row) === filters.tabId) &&
      (filters.type === null || row.itemKind === filters.type) &&
      (filters.subtype === null || row.category === filters.subtype),
  );
  if (filters.sort === "name") return [...shown].sort((a, b) => compareNames(a.name, b.name));
  if (filters.sort === "last") {
    // Largest last count first; an item with no last count goes last, in shelf order.
    return shown
      .map((row, index) => ({ row, index }))
      .sort((a, b) => {
        const left = lastValue(a.row);
        const right = lastValue(b.row);
        if (left === undefined && right === undefined) return a.index - b.index;
        if (left === undefined) return 1;
        if (right === undefined) return -1;
        return right - left || a.index - b.index;
      })
      .map((entry) => entry.row);
  }
  return [...shown];
}

/** The tab after this one, or undefined at the end. */
export function nextTab(tabs: readonly TableTab[], currentId: string): TableTab | undefined {
  const index = tabs.findIndex((tab) => tab.id === currentId);
  if (index < 0) return undefined;
  for (let i = index + 1; i < tabs.length; i += 1) {
    const candidate = tabs[i];
    if (candidate !== undefined && candidate.total > 0) return candidate;
  }
  return undefined;
}

/** The distinct values a picker offers, from what exists, sorted A to Z by label. */
export function pickerValues(
  rows: readonly TableRow[],
  field: "itemKind" | "category",
  label: (value: string) => string,
  within?: { itemKind: string | null },
): { value: string; label: string }[] {
  const seen = new Set<string>();
  for (const row of rows) {
    if (within?.itemKind != null && row.itemKind !== within.itemKind) continue;
    seen.add(row[field]);
  }
  return [...seen]
    .map((value) => ({ value, label: label(value) }))
    .sort((a, b) => compareNames(a.label, b.label));
}

/** The A to Z strip: each letter that starts a name, A to Z, then # for the rest. */
export function alphabetLetters(names: readonly string[]): string[] {
  const letters = new Set(names.map(firstLetter));
  const sorted = [...letters].filter((letter) => letter !== "#").sort();
  return letters.has("#") ? [...sorted, "#"] : sorted;
}

export function firstLetter(name: string): string {
  const letter = name.trim().charAt(0).toUpperCase();
  return /^[A-Z]$/.test(letter) ? letter : "#";
}

/** Where an item sits on its shelf: "7 of 14 in Well", from the rows in shelf order. */
export function shelfPosition(
  rows: readonly TableRow[],
  itemId: string,
): { index: number; total: number; sectionName: string } | undefined {
  const target = rows.find((row) => row.id === itemId);
  if (target === undefined) return undefined;
  const inSection = rows.filter((row) => row.sectionId === target.sectionId);
  return {
    index: inSection.findIndex((row) => row.id === itemId) + 1,
    total: inSection.length,
    sectionName: target.sectionName ?? "Unassigned",
  };
}

export function countedTotals(rows: readonly TableRow[]): { counted: number; total: number } {
  return { counted: rows.filter((row) => row.entry !== null).length, total: rows.length };
}

// ---------------------------------------------------------------------
// Review
// ---------------------------------------------------------------------

export type ReviewFilter = "all" | "differences" | "not-counted";

export interface ReviewRow {
  /** The item, for a row that can be counted from here. Null for a counted line. */
  itemId: string | null;
  name: string;
  sectionName: string | null;
  counted: boolean;
  /** Figures as shown, or null for N/A. */
  expected: string | null;
  countedText: string | null;
  /** Counted minus expected in bottles when both are known in bottles; otherwise a figure in `unit`. */
  difference: number | null;
  differenceText: string;
  unit: string;
  /** INVDESIGN1-S2: expected and counted as numbers in one shared unit, for the percent. Null when not counted. */
  expectedNumber: number | null;
  countedNumber: number | null;
  /** Percent off expected, one decimal (positive over, negative short); null with nothing expected or nothing counted. */
  percent: number | null;
  /** True when the row is off by COUNT_CHECK_PERCENT or more: it sorts first and reads Check. */
  check: boolean;
  /** Whole cents of the difference in value (counted minus expected), valued like Stock value; null when either side has no
   * usable cost or the server sent no values (a caller without the Stock value right). */
  costCents: number | null;
  expectedValueCents: number | null;
  countedValueCents: number | null;
}

/** The numbers and verdict every counted row carries, from the two quantities in one unit and the optional dollar values. */
function measured(
  expected: number,
  counted: number,
  dto: Pick<CountSessionReviewRowDto, "expectedValueCents" | "countedValueCents">,
): Pick<
  ReviewRow,
  | "expectedNumber"
  | "countedNumber"
  | "percent"
  | "check"
  | "costCents"
  | "expectedValueCents"
  | "countedValueCents"
> {
  const percent = percentOff(expected, counted);
  const expectedValue = dto.expectedValueCents ?? null;
  const countedValue = dto.countedValueCents ?? null;
  return {
    expectedNumber: expected,
    countedNumber: counted,
    percent,
    check: needsCheck(percent),
    costCents: expectedValue !== null && countedValue !== null ? countedValue - expectedValue : null,
    expectedValueCents: expectedValue,
    countedValueCents: countedValue,
  };
}

/** Up to two decimals, trailing zeros trimmed: 4.2 reads "4.2", 6 reads "6". */
export function formatFigure(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return String(Object.is(rounded, -0) ? 0 : rounded);
}

function signed(value: number): string {
  if (value === 0) return "0";
  return `${value > 0 ? "+" : "−"}${formatFigure(Math.abs(value))}`;
}

function inBottles(
  quantity: string,
  unit: string,
  bottleOunces: string | null,
): number | undefined {
  const value = Number(quantity);
  if (!Number.isFinite(value)) return undefined;
  if (unit === "bottle" || unit === "unit") return value;
  if (unit === "oz" && bottleOunces !== null) {
    const perBottle = Number(bottleOunces);
    return Number.isFinite(perBottle) && perBottle > 0 ? value / perBottle : undefined;
  }
  return undefined;
}

/** A counted line from the review route. Figures are shown in bottles whenever the line is counted in bottles or the
 * item has a bottle size; a line that cannot be read as bottles keeps its own unit. */
export function reviewRowFromCounted(
  dto: CountSessionReviewRowDto,
  bottleOuncesByName: ReadonlyMap<string, string | null>,
): ReviewRow {
  if (dto.bottleView !== null) {
    const counted = Number(dto.bottleView.countedQuantity);
    const expected = Number(dto.bottleView.expectedQuantity);
    const difference =
      Number(dto.previewVariance) === 0 ? 0 : Number(dto.bottleView.previewVariance);
    // A variance under a hundredth of a bottle still reads as a difference, never as zero.
    const tiny = Number(dto.previewVariance) !== 0 && difference === 0;
    const shown = tiny ? Math.sign(Number(dto.previewVariance)) * 0.01 : difference;
    return {
      itemId: dto.liquorProductId,
      name: dto.liquorProductName,
      sectionName: dto.sectionName,
      counted: true,
      expected: formatFigure(expected),
      countedText: formatFigure(counted),
      difference: shown,
      differenceText: signed(shown),
      unit: "bottle",
      ...measured(expected, counted, dto),
    };
  }
  if (dto.unit === "bottle" || dto.unit === "unit") {
    // Stored as bottles already (a count made on the table in a shop that keeps raw units): no conversion, no suffix.
    const counted = Number(dto.countedQuantity);
    const expected = Number(dto.expectedQuantity);
    const difference = Number(dto.previewVariance);
    return {
      itemId: dto.liquorProductId,
      name: dto.liquorProductName,
      sectionName: dto.sectionName,
      counted: true,
      expected: formatFigure(expected),
      countedText: formatFigure(counted),
      difference: Number.isFinite(difference) ? difference : null,
      differenceText: Number.isFinite(difference)
        ? signed(Math.round(difference * 100) / 100)
        : "-",
      unit: "bottle",
      ...measured(expected, counted, dto),
    };
  }
  const bottleOunces = bottleOuncesByName.get(dto.liquorProductName) ?? null;
  const countedBottles = inBottles(dto.countedQuantity, dto.unit, bottleOunces);
  const expectedBottles = inBottles(dto.expectedQuantity, dto.unit, bottleOunces);
  if (countedBottles !== undefined && expectedBottles !== undefined && dto.unit === "oz") {
    const difference = countedBottles - expectedBottles;
    return {
      itemId: dto.liquorProductId,
      name: dto.liquorProductName,
      sectionName: dto.sectionName,
      counted: true,
      expected: formatFigure(expectedBottles),
      countedText: formatFigure(countedBottles),
      difference,
      differenceText: signed(Math.round(difference * 100) / 100),
      unit: "bottle",
      ...measured(expectedBottles, countedBottles, dto),
    };
  }
  const difference = Number(dto.previewVariance);
  return {
    itemId: dto.liquorProductId,
    name: dto.liquorProductName,
    sectionName: dto.sectionName,
    counted: true,
    expected: `${formatFigure(Number(dto.expectedQuantity))} ${dto.unit}`,
    countedText: `${formatFigure(Number(dto.countedQuantity))} ${dto.unit}`,
    difference: Number.isFinite(difference) ? difference : null,
    differenceText: Number.isFinite(difference) ? `${signed(difference)} ${dto.unit}` : "-",
    unit: dto.unit,
    ...measured(Number(dto.expectedQuantity), Number(dto.countedQuantity), dto),
  };
}

/** An item with no count. `expected` is in bottles when the expected figure is known, else a dash. */
export function reviewRowFromUncounted(
  item: Pick<CountUncountedItemDto, "id" | "name" | "countSectionName">,
  expected: string | null,
): ReviewRow {
  return {
    itemId: item.id,
    name: item.name,
    sectionName: item.countSectionName,
    counted: false,
    expected,
    countedText: null,
    difference: null,
    differenceText: "-",
    unit: "bottle",
    expectedNumber: null,
    countedNumber: null,
    percent: null,
    check: false,
    costCents: null,
    expectedValueCents: null,
    countedValueCents: null,
  };
}

export function isDifference(row: ReviewRow): boolean {
  return row.counted && row.difference !== null && row.difference !== 0;
}

export function filterReviewRows(rows: readonly ReviewRow[], filter: ReviewFilter): ReviewRow[] {
  if (filter === "differences") return rows.filter(isDifference);
  if (filter === "not-counted") return rows.filter((row) => !row.counted);
  return [...rows];
}

/** INVDESIGN1-S2: Check rows first (the largest percent off leading), then the items not counted, then every other
 * difference by size, then A to Z. */
export function sortReviewRows(rows: readonly ReviewRow[]): ReviewRow[] {
  return [...rows].sort((a, b) => {
    if (a.check !== b.check) return a.check ? -1 : 1;
    if (a.check && b.check) {
      const byPercent = Math.abs(b.percent ?? 0) - Math.abs(a.percent ?? 0);
      if (byPercent !== 0) return byPercent;
    }
    if (a.counted !== b.counted) return a.counted ? 1 : -1;
    const left = Math.abs(a.difference ?? 0);
    const right = Math.abs(b.difference ?? 0);
    return right - left || compareNames(a.name, b.name);
  });
}

/** The review's money tiles, summed over the counted rows that have both values: what was expected, what was counted and the
 * gap, as whole cents. Null when no counted row carries values (a caller without the Stock value right, or no costs). */
export function reviewValueTotals(
  rows: readonly ReviewRow[],
): { expectedCents: number; countedCents: number; unvalued: number } | null {
  let expectedCents = 0;
  let countedCents = 0;
  let valued = 0;
  let unvalued = 0;
  for (const row of rows) {
    if (!row.counted) continue;
    if (row.expectedValueCents === null || row.countedValueCents === null) {
      unvalued += 1;
      continue;
    }
    expectedCents += row.expectedValueCents;
    countedCents += row.countedValueCents;
    valued += 1;
  }
  return valued === 0 ? null : { expectedCents, countedCents, unvalued };
}

/** Short and Over only add figures that are in bottles, so a unit that cannot be read as bottles never mixes in. */
export function reviewTotals(rows: readonly ReviewRow[]): { short: number; over: number } {
  let short = 0;
  let over = 0;
  for (const row of rows) {
    if (!row.counted || row.unit !== "bottle" || row.difference === null) continue;
    if (row.difference < 0) short += row.difference;
    if (row.difference > 0) over += row.difference;
  }
  return { short: Math.round(short * 100) / 100, over: Math.round(over * 100) / 100 };
}

export function signedFigure(value: number): string {
  return signed(value);
}

/** "+$5.59", "-$9.30", "$0.00": whole cents, with the sign shown for any gap. */
export function signedMoney(cents: number): string {
  const magnitude = Math.abs(cents);
  const dollars = Math.trunc(magnitude / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const text = `$${dollars}.${String(magnitude % 100).padStart(2, "0")}`;
  if (cents === 0) return text;
  return `${cents < 0 ? "-" : "+"}${text}`;
}

/** "+8.3%", "-37.5%", "0%", or a dash when there was nothing expected to measure against. */
export function signedPercent(percent: number | null): string {
  if (percent === null) return "-";
  if (percent === 0) return "0%";
  return `${percent > 0 ? "+" : "-"}${Math.abs(percent).toFixed(1)}%`;
}

/** INVDESIGN1-S2: what the review said when the count was closed, carried to the after-count screen. The dollars are the review's
 * own tiles (null when the caller had no values), so the "off by" on the after-count screen can never differ from the review. */
export interface CountClosedSummary {
  readonly sessionId: string;
  readonly itemsCounted: number;
  readonly notCounted: number;
  readonly differenceCents: number | null;
  readonly percent: number | null;
}

export function closedSummary(sessionId: string, rows: readonly ReviewRow[]): CountClosedSummary {
  const counted = rows.filter((row) => row.counted).length;
  const money = reviewValueTotals(rows);
  const gap = money === null ? null : dollarVariance(money.expectedCents, money.countedCents);
  return {
    sessionId,
    itemsCounted: counted,
    notCounted: rows.length - counted,
    differenceCents: gap === null ? null : gap.differenceCents,
    percent: gap === null ? null : gap.percent,
  };
}

/** INVPOLISH1: "Par 8" for the item being counted, or null when it has no par, or its par is in a unit the count does not
 * speak (only bottles, cans and kegs are counted in their own container, so only those rows can say what par means). */
export function parText(row: Pick<TableRow, "par" | "containerType">): string | null {
  if (row.par === undefined || row.par === null || row.par === "") return null;
  if (row.containerType !== "bottle" && row.containerType !== "can" && row.containerType !== "keg") return null;
  return `Par ${row.par}`;
}
